/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from "react-query";
import { useDispatch } from "react-redux";

import { ApiResponse } from "../../interfaces/Api";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../useCurrentResourceCentre";
import { IShiftAssignment } from "../../interfaces/ShiftAssignmentInterface";
import {
  getShiftAssignments,
  postShiftAssignment,
  patchShiftAssignment,
  deleteShiftAssignment
} from "../../api/shiftAssignment";

export const shiftAssignmentsQueryKey = "shiftAssignments";

export const useShiftAssignments = (params?: {
  fromDate?: string;
  toDate?: string;
}): UseQueryResult<Array<IShiftAssignment>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [shiftAssignmentsQueryKey, resourceCentre?.id, params],
    queryFn: async () => {
      if (!resourceCentre?.id) return [];

      const response = await getShiftAssignments({
        resourceCentreId: Number(resourceCentre.id),
        ...params
      });

      return response.data || [];
    },
    enabled: !!resourceCentre?.id
  });
};

export const usePostShiftAssignment = (): UseMutationResult<
  ApiResponse<IShiftAssignment>,
  Error,
  Partial<IShiftAssignment>,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<IShiftAssignment>) => postShiftAssignment(data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [shiftAssignmentsQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Shift assignment created successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to create shift assignment",
          variant: "error"
        })
      );
    }
  });
};

export const usePatchShiftAssignment = (): UseMutationResult<
  ApiResponse<IShiftAssignment>,
  Error,
  { id: number | string; data: Partial<IShiftAssignment> },
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => patchShiftAssignment({ id, data }),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [shiftAssignmentsQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Shift assignment updated successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to update shift assignment",
          variant: "error"
        })
      );
    }
  });
};

export const useDeleteShiftAssignment = (): UseMutationResult<
  ApiResponse<null>,
  Error,
  number | string,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteShiftAssignment(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [shiftAssignmentsQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Shift assignment deleted successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to delete shift assignment",
          variant: "error"
        })
      );
    }
  });
};
